import React, { useState, useEffect, useRef } from 'react';
import { BookOpen, Zap, Trophy, Search, XCircle } from 'lucide-react';
import api from './api';

const ProblemMenu = ({ onSelectCategory, user }) => {
  const [categories, setCategories] = useState([]);
  const [totalProblems, setTotalProblems] = useState(0);
  const [totalSolved, setTotalSolved] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showAdminMenu, setShowAdminMenu] = useState(false);
  const [showJumpDialog, setShowJumpDialog] = useState(false);
  const [jumpSearchQuery, setJumpSearchQuery] = useState('');
  const [allProblems, setAllProblems] = useState([]);
  const jumpInputRef = useRef(null);

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await api.get('/api/problems/categories');
      console.log('Categories data:', response.data);
      setCategories(response.data.categories);
      setTotalProblems(response.data.totalProblems);
      setTotalSolved(response.data.totalSolved);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    } finally {
      setLoading(false);
    }
  };

  const getCategoryIcon = (tag) => {
    if (['easy', 'medium', 'hard'].includes(tag.toLowerCase())) {
      return Zap;
    }
    return BookOpen;
  };

  const getCategoryColor = (tag) => {
    const tagLower = tag.toLowerCase();
    if (tagLower === 'easy') return 'green';
    if (tagLower === 'medium') return 'yellow';
    if (tagLower === 'hard') return 'red';
    return 'blue';
  };

  const getColorClasses = (color) => {
    const colors = {
      green: {
        border: 'border-green-500',
        bg: 'bg-green-500/10',
        text: 'text-green-400',
        progressBg: 'bg-green-400',
        hover: 'hover:border-green-400'
      },
      yellow: {
        border: 'border-yellow-500',
        bg: 'bg-yellow-500/10',
        text: 'text-yellow-400',
        progressBg: 'bg-yellow-400',
        hover: 'hover:border-yellow-400'
      },
      red: {
        border: 'border-red-500',
        bg: 'bg-red-500/10',
        text: 'text-red-400',
        progressBg: 'bg-red-400',
        hover: 'hover:border-red-400'
      },
      blue: {
        border: 'border-blue-500',
        bg: 'bg-blue-500/10',
        text: 'text-blue-400',
        progressBg: 'bg-blue-400',
        hover: 'hover:border-blue-400'
      }
    };
    return colors[color];
  };

  const handleClearTestCategory = async () => {
    if (!window.confirm('Are you sure you want to clear all solved problems in the "Test" category?')) {
      return;
    }

    try {
      await api.post('/api/admin/clear-category-progress', { category: 'test' });
      alert('Successfully cleared all Test category progress!');
      setShowAdminMenu(false);

      // Refresh the categories
      fetchCategories();
    } catch (error) {
      console.error('Failed to clear category progress:', error);
      alert('Failed to clear category progress. Please try again.');
    }
  };

  // Fetch all problems for jump-to-problem feature
  const fetchAllProblems = async () => {
    try {
      const response = await api.get('/api/problems');
      setAllProblems(response.data);
    } catch (error) {
      console.error('Failed to fetch all problems:', error);
    }
  };

  // Open jump dialog from admin menu
  const openJumpDialog = () => {
    setShowAdminMenu(false);
    setShowJumpDialog(true);
    setJumpSearchQuery('');
    if (allProblems.length === 0) {
      fetchAllProblems();
    }
    // Focus input after dialog opens
    setTimeout(() => {
      if (jumpInputRef.current) {
        jumpInputRef.current.focus();
      }
    }, 100);
  };

  // Filter problems based on search query
  const getFilteredProblems = () => {
    if (!jumpSearchQuery.trim()) {
      return allProblems.slice(0, 10); // Show first 10 if no query
    }

    const query = jumpSearchQuery.toLowerCase();
    return allProblems.filter(p =>
      p.title.toLowerCase().includes(query) ||
      p.problemId.toLowerCase().includes(query) ||
      (p.tags && p.tags.some(tag => tag.toLowerCase().includes(query)))
    ).slice(0, 10); // Limit to 10 results
  };

  // Jump to selected problem
  const jumpToProblem = (problem) => {
    setShowJumpDialog(false);
    setJumpSearchQuery('');
    // Navigate directly to the problem by selecting its primary category
    if (problem.tags && problem.tags.length > 0) {
      onSelectCategory(problem.tags[0], problem.problemId);
    }
  };

  // Separate difficulty and specialization categories
  const difficultyCategories = categories.filter(cat =>
    ['easy', 'medium', 'hard'].includes(cat.tag.toLowerCase())
  );

  const specializationCategories = categories.filter(cat =>
    !['easy', 'medium', 'hard'].includes(cat.tag.toLowerCase())
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 flex items-center justify-center">
        <div className="text-white text-2xl">Loading problems...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 p-6">
      {/* Admin Menu Modal */}
      {showAdminMenu && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-slate-800 rounded-lg p-6 border-2 border-yellow-400 max-w-md w-full">
            <h3 className="text-2xl font-bold text-white mb-4">Admin Menu</h3>
            <div className="space-y-3">
              <button
                onClick={handleClearTestCategory}
                className="w-full bg-red-600 hover:bg-red-700 text-white px-4 py-3 rounded-lg font-semibold transition text-left"
              >
                Clear All Solved Problems in "Test" Category
              </button>
              <button
                onClick={openJumpDialog}
                className="w-full bg-purple-600 hover:bg-purple-700 text-white px-4 py-3 rounded-lg font-semibold transition text-left flex items-center gap-2"
              >
                <Search size={20} />
                Jump to Problem
              </button>
            </div>
            <div className="flex gap-3 justify-end mt-6">
              <button
                onClick={() => setShowAdminMenu(false)}
                className="bg-slate-600 hover:bg-slate-700 text-white px-6 py-2 rounded-lg font-semibold transition"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-6xl mx-auto">
        {/* Header with stats */}
        <div className="text-center mb-8">
          <h1
            className={`text-5xl font-bold text-white mb-2 ${user?.isAdmin ? 'cursor-pointer hover:text-yellow-400 transition-colors' : ''}`}
            onClick={() => user?.isAdmin && setShowAdminMenu(true)}
            title={user?.isAdmin ? 'Click for admin menu' : ''}
          >
            Kodala
          </h1>
          <h2 className="text-3xl font-bold text-slate-300 mb-3">Choose Your Challenge</h2>
          <p className="text-slate-400 text-lg mb-4">
            Select a problem category to start solving
          </p>
          <div className="flex items-center justify-center gap-4">
            <div className="bg-slate-800 border border-slate-700 rounded-lg px-4 py-2 flex items-center gap-2">
              <Trophy className="w-5 h-5 text-yellow-400" />
              <span className="text-white font-semibold">
                {totalSolved} / {totalProblems}
              </span>
              <span className="text-slate-400">solved</span>
            </div>
          </div>

          {/* Provide Feedback Button */}
          <div className="mt-4">
            <a
              href="mailto:jbaramidze@gmail.com?subject=Kodala Feedback"
              className="inline-flex items-center gap-2 bg-slate-700 hover:bg-slate-600 text-white px-6 py-2 rounded-lg font-semibold transition"
            >
              <span>📧</span>
              Provide Feedback
            </a>
          </div>
        </div>

        {/* Difficulty Levels */}
        {difficultyCategories.length > 0 && (
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-white mb-4">Difficulty Levels</h2>
            <div className="grid md:grid-cols-3 gap-4">
              {difficultyCategories.map((category) => {
                const Icon = getCategoryIcon(category.tag);
                const color = getCategoryColor(category.tag);
                const colors = getColorClasses(color);
                const progress = category.total > 0 ? (category.solved / category.total) * 100 : 0;

                return (
                  <button
                    key={category.tag}
                    onClick={() => onSelectCategory(category.tag)}
                    className={`bg-slate-800 border-2 ${colors.border} ${colors.bg} ${colors.hover} rounded-lg p-6 transition-all transform hover:scale-105 text-left`}
                  >
                    <div className="flex items-center justify-between mb-3">
                      <Icon className={`w-8 h-8 ${colors.text}`} />
                      <div className="text-right">
                        <div className="text-2xl font-bold text-white">
                          {category.solved}/{category.total}
                        </div>
                        <div className="text-xs text-slate-400">problems</div>
                      </div>
                    </div>
                    <h3 className="text-xl font-bold text-white mb-2 capitalize">
                      {category.tag}
                    </h3>
                    {/* Progress bar */}
                    <div className="w-full bg-slate-700 rounded-full h-2 mb-2">
                      <div
                        className={`h-2 rounded-full ${colors.progressBg}`}
                        style={{ width: `${progress}%`, minWidth: progress > 0 ? '8px' : '0' }}
                      />
                    </div>
                    <p className="text-xs text-slate-400">
                      {progress.toFixed(0)}% complete
                    </p>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Specializations */}
        {specializationCategories.length > 0 && (
          <div>
            <h2 className="text-2xl font-bold text-white mb-4">Specializations</h2>
            <div className="grid md:grid-cols-3 lg:grid-cols-4 gap-4">
              {specializationCategories.map((category) => {
                const Icon = getCategoryIcon(category.tag);
                const colors = getColorClasses('blue');
                const progress = category.total > 0 ? (category.solved / category.total) * 100 : 0;
                console.log(`Category ${category.tag}: ${category.solved}/${category.total} = ${progress}%`);

                return (
                  <button
                    key={category.tag}
                    onClick={() => onSelectCategory(category.tag)}
                    className={`bg-slate-800 border-2 ${colors.border} ${colors.bg} ${colors.hover} rounded-lg p-5 transition-all transform hover:scale-105 text-left`}
                  >
                    <div className="flex items-center justify-between mb-3">
                      <Icon className={`w-6 h-6 ${colors.text}`} />
                      <div className="text-right">
                        <div className="text-lg font-bold text-white">
                          {category.solved}/{category.total}
                        </div>
                      </div>
                    </div>
                    <h3 className="text-base font-bold text-white mb-2 capitalize">
                      {category.tag.replace(/-/g, ' ')}
                    </h3>
                    {/* Progress bar */}
                    <div className="w-full bg-slate-700 rounded-full h-1.5">
                      <div
                        className={`h-1.5 rounded-full ${colors.progressBg}`}
                        style={{ width: `${progress}%`, minWidth: progress > 0 ? '8px' : '0' }}
                      />
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {categories.length === 0 && (
          <div className="text-center text-slate-400 text-lg">
            No problems available yet. Check back soon!
          </div>
        )}
      </div>

      {/* Jump to Problem Dialog */}
      {showJumpDialog && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" onClick={() => setShowJumpDialog(false)}>
          <div className="bg-slate-800 rounded-lg p-6 max-w-2xl w-full max-h-[80vh] overflow-hidden flex flex-col border border-slate-700" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-white flex items-center gap-2">
                <Search size={24} />
                Jump to Problem
              </h3>
              <button
                onClick={() => setShowJumpDialog(false)}
                className="text-slate-400 hover:text-white transition"
              >
                <XCircle size={24} />
              </button>
            </div>

            {/* Search Input */}
            <div className="mb-4">
              <input
                ref={jumpInputRef}
                type="text"
                value={jumpSearchQuery}
                onChange={(e) => setJumpSearchQuery(e.target.value)}
                placeholder="Search by title, ID, or tag..."
                className="w-full bg-slate-900 text-white px-4 py-3 rounded-lg border border-slate-600 focus:border-purple-500 focus:outline-none"
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && getFilteredProblems().length > 0) {
                    jumpToProblem(getFilteredProblems()[0]);
                  } else if (e.key === 'Escape') {
                    setShowJumpDialog(false);
                  }
                }}
              />
              <p className="text-slate-400 text-sm mt-2">
                {jumpSearchQuery.trim() ? `Found ${getFilteredProblems().length} problem(s)` : `Showing ${Math.min(allProblems.length, 10)} of ${allProblems.length} problems`}
              </p>
            </div>

            {/* Problem List */}
            <div className="flex-1 overflow-y-auto space-y-2">
              {getFilteredProblems().length === 0 ? (
                <div className="text-center text-slate-400 py-8">
                  {allProblems.length === 0 ? 'Loading problems...' : 'No problems found'}
                </div>
              ) : (
                getFilteredProblems().map((problem) => (
                  <button
                    key={problem.problemId}
                    onClick={() => jumpToProblem(problem)}
                    className="w-full text-left bg-slate-900 hover:bg-slate-700 p-4 rounded-lg border border-slate-600 hover:border-purple-500 transition group"
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <h4 className="text-white font-semibold group-hover:text-purple-400 transition">
                          {problem.title}
                        </h4>
                        <p className="text-slate-400 text-sm mt-1">ID: {problem.problemId}</p>
                      </div>
                      <div className="flex flex-wrap gap-1">
                        {problem.tags && problem.tags.slice(0, 3).map((tag, idx) => (
                          <span
                            key={idx}
                            className="px-2 py-1 bg-slate-700 text-slate-300 rounded text-xs font-semibold capitalize"
                          >
                            {tag}
                          </span>
                        ))}
                        {problem.tags && problem.tags.length > 3 && (
                          <span className="px-2 py-1 bg-slate-700 text-slate-300 rounded text-xs font-semibold">
                            +{problem.tags.length - 3}
                          </span>
                        )}
                      </div>
                    </div>
                  </button>
                ))
              )}
            </div>

            {/* Keyboard Shortcuts Help */}
            <div className="mt-4 pt-4 border-t border-slate-700">
              <p className="text-slate-400 text-xs">
                <span className="font-semibold">Tip:</span> Press <kbd className="px-2 py-1 bg-slate-900 rounded border border-slate-600 font-mono">Enter</kbd> to jump to first result, <kbd className="px-2 py-1 bg-slate-900 rounded border border-slate-600 font-mono">Esc</kbd> to close
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProblemMenu;
